import { adaptSnapshotToDatasets } from "@/lib/ai/visualization/data/adapters/adapter-registry";
import { DEFAULT_DATASET_ID } from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import type {
  VizDataset,
  VizDatasetOutput,
} from "@/lib/ai/visualization/data/schema";
import type { VizToolSnapshot } from "@/lib/ai/visualization/runtime/snapshot-store";

/**
 * dataset 出力構築時に必要な実行コンテキスト。
 */
type VizDatasetOutputOptions = {
  toolCallId: string;
};

function isAsciiLower(value: string): boolean {
  return value >= "a" && value <= "z";
}

function isAsciiDigit(value: string): boolean {
  return value >= "0" && value <= "9";
}

function trimHyphen(value: string): string {
  let start = 0;
  let end = value.length;
  while (start < end && value[start] === "-") start += 1;
  while (end > start && value[end - 1] === "-") end -= 1;
  return value.slice(start, end);
}

function normalizeId(value: string): string {
  const lowered = value.toLowerCase();
  let result = "";
  let lastWasHyphen = false;

  for (const char of lowered) {
    if (
      isAsciiLower(char) ||
      isAsciiDigit(char) ||
      char === "_" ||
      char === "-"
    ) {
      result += char;
      lastWasHyphen = char === "-";
      continue;
    }
    if (!lastWasHyphen) {
      result += "-";
      lastWasHyphen = true;
    }
  }

  return trimHyphen(result);
}

function sanitizeDatasetId(value: string): string {
  const normalized = normalizeId(value);
  return normalized.length > 0 ? normalized : DEFAULT_DATASET_ID;
}

function sanitizeOptionalId(value?: string | null): string | null {
  if (!value || value.length === 0) return null;
  const normalized = normalizeId(value);
  return normalized.length > 0 ? normalized : null;
}

function resolveDatasetIdPrefix(options: VizDatasetOutputOptions): string {
  const normalized = sanitizeOptionalId(options.toolCallId);
  if (normalized) return normalized;
  throw new Error("viz_render dataset output requires a toolCallId");
}

function ensureUniqueId(existing: Set<string>, base: string): string {
  let suffix = 0;
  while (true) {
    const candidate = suffix > 0 ? `${base}-${suffix}` : base;
    if (!existing.has(candidate)) {
      existing.add(candidate);
      return candidate;
    }
    suffix += 1;
  }
}

function toRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * snapshot から query context をそのまま取り出す。
 *
 * @remarks
 * dataset 出力と一緒にしか使われない helper なので、このファイルへ寄せて
 * singleton helper file を増やさない。
 */
function buildDatasetContext(
  snapshot: VizToolSnapshot,
): NonNullable<VizDataset["context"]> | undefined {
  const output = toRecord(snapshot.output);
  const queryContext =
    toRecord(output?.context) ??
    toRecord(output?.applied) ??
    toRecord(snapshot.input);
  if (!queryContext) return undefined;

  return {
    queryContext,
  };
}

/**
 * primary dataset id を安定順で返す。
 *
 * @remarks
 * authoring 前段で意味論ヒューリスティックを適用しない方針のため、
 * ここでは「最初に生成された有効 dataset」を返すだけに限定する。
 */
function selectPrimaryDatasetId(args: {
  orderedDatasetIds: string[];
  datasets: Record<string, VizDataset>;
}): string | null {
  for (const datasetId of args.orderedDatasetIds) {
    if (args.datasets[datasetId]) return datasetId;
  }
  return null;
}

/**
 * 複数ツールのスナップショットから描画用 dataset 群を構築する。
 *
 * primaryDatasetId は dataset 生成順のみで決定する。
 *
 * @remarks
 * LLM 前段の先回り推測を避けるため、能力ベースのヒューリスティック選別は行わない。
 * dataset が1件も生成できない場合は補完せず空集合を返し、上位層で fail-fast させる。
 */
export function buildVizDatasetOutput(
  snapshots: VizToolSnapshot[],
  options: VizDatasetOutputOptions,
): VizDatasetOutput {
  const datasetIds = new Set<string>();
  const datasets: Record<string, VizDataset> = {};
  const orderedDatasetIds: string[] = [];
  const datasetIdPrefix = resolveDatasetIdPrefix(options);

  snapshots.forEach((snapshot) => {
    const built = adaptSnapshotToDatasets(snapshot);
    if (built.length === 0) return;
    const context = buildDatasetContext(snapshot);

    for (const dataset of built) {
      const base = `${datasetIdPrefix}-${sanitizeDatasetId(dataset.id)}`;
      const id = ensureUniqueId(datasetIds, base);
      const normalizedDataset: VizDataset = {
        ...dataset,
        id,
        baseId: dataset.id,
        ...(context ? { context } : null),
      };
      datasets[id] = normalizedDataset;
      orderedDatasetIds.push(id);
    }
  });

  if (Object.keys(datasets).length === 0) {
    // dataset が1件も作れない場合は補完せず、上位で fail-fast させる。
    return {
      primaryDatasetId: `${datasetIdPrefix}-none`,
      datasets: {},
    };
  }

  const primaryDatasetId =
    selectPrimaryDatasetId({
      orderedDatasetIds,
      datasets,
    }) ?? orderedDatasetIds[0]!;

  return {
    primaryDatasetId,
    datasets,
  };
}
